import React from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Skeleton, TextField, InputAdornment,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { Search } from 'lucide-react';

const ff = "'Poppins', sans-serif";

// ─── Styled Table Container ─────────────────────────────────────
export const StyledTableContainer = ({ children, ...props }) => (
  <TableContainer
    component={Paper}
    elevation={0}
    sx={{
      borderRadius: '12px',
      border: '1px solid #EDF2F7',
      overflow: 'hidden',
      '& .MuiTable-root': {
        borderCollapse: 'separate',
        borderSpacing: 0,
      },
      '& .MuiTableHead-root .MuiTableCell-root': {
        fontFamily: ff,
        fontSize: 12,
        fontWeight: 600,
        color: '#718096',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        background: '#FAFBFC',
        borderBottom: '1px solid #EDF2F7',
        py: 1.5,
        px: 2,
        whiteSpace: 'nowrap',
      },
      '& .MuiTableBody-root .MuiTableCell-root': {
        fontFamily: ff,
        fontSize: 13,
        color: '#2D3748',
        borderBottom: '1px solid #F7FAFC',
        py: 1.5,
        px: 2,
      },
      '& .MuiTableBody-root .MuiTableRow-root': {
        transition: 'background 0.1s',
        '&:hover': {
          background: '#FAFBFC',
        },
        '&:last-child .MuiTableCell-root': {
          borderBottom: 'none',
        },
      },
      '& .MuiTablePagination-root': {
        fontFamily: ff,
        borderTop: '1px solid #EDF2F7',
      },
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        fontFamily: ff,
        fontSize: 12,
        color: '#718096',
      },
    }}
    {...props}
  >
    {children}
  </TableContainer>
);

// ─── Table Skeleton Loading ─────────────────────────────────────
export const TableSkeleton = ({ columns = 5, rows = 6 }) => (
  <StyledTableContainer>
    <Table>
      <TableHead>
        <TableRow>
          {Array.from({ length: columns }).map((_, i) => (
            <TableCell key={i}>
              <Skeleton variant="text" width={80} height={16} />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: rows }).map((_, rowIdx) => (
          <TableRow key={rowIdx}>
            {Array.from({ length: columns }).map((_, colIdx) => (
              <TableCell key={colIdx}>
                <Skeleton
                  variant="text"
                  width={colIdx === 0 ? 120 : `${60 + Math.random() * 40}%`}
                  height={18}
                  sx={{ borderRadius: '4px' }}
                />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </StyledTableContainer>
);

// ─── Filter Bar ─────────────────────────────────────────────────
export const FilterBar = ({ children }) => (
  <Box sx={{
    display: 'flex',
    gap: 1.5,
    mb: 2.5,
    flexWrap: 'wrap',
    alignItems: 'center',
  }}>
    {children}
  </Box>
);

// ─── Search Input ───────────────────────────────────────────────
export const SearchInput = ({ value, onChange, placeholder = 'Search...', ...props }) => (
  <TextField
    size="small"
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    sx={{
      minWidth: 280,
      '& .MuiOutlinedInput-root': {
        fontFamily: ff,
        fontSize: 13,
        borderRadius: '10px',
        background: '#FAFBFC',
        '& fieldset': { borderColor: '#EDF2F7' },
        '&:hover fieldset': { borderColor: '#CBD5E0' },
        '&.Mui-focused fieldset': { borderColor: '#32A6D8' },
      },
    }}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <Search size={16} color="#A0AEC0" />
        </InputAdornment>
      ),
    }}
    {...props}
  />
);

// ─── Filter Select ──────────────────────────────────────────────
export const FilterSelect = ({ label, value, onChange, options, minWidth = 150 }) => (
  <FormControl
    size="small"
    sx={{
      minWidth,
      '& .MuiOutlinedInput-root': {
        fontFamily: ff,
        fontSize: 13,
        borderRadius: '10px',
        background: '#FAFBFC',
        '& fieldset': { borderColor: '#EDF2F7' },
        '&:hover fieldset': { borderColor: '#CBD5E0' },
        '&.Mui-focused fieldset': { borderColor: '#32A6D8' },
      },
      '& .MuiInputLabel-root': { fontFamily: ff, fontSize: 13 },
    }}
  >
    <InputLabel>{label}</InputLabel>
    <Select value={value} label={label} onChange={onChange}>
      {options.map((opt) => (
        <MenuItem key={typeof opt === 'string' ? opt : opt.value} value={typeof opt === 'string' ? opt : opt.value}
          sx={{ fontFamily: ff, fontSize: 13 }}
        >
          {typeof opt === 'string' ? opt : opt.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

// ─── Styled Pagination ──────────────────────────────────────────
export const StyledPagination = ({ count, page, rowsPerPage, onPageChange, onRowsPerPageChange, rowsPerPageOptions = [5, 10, 25, 50] }) => (
  <TablePagination
    component="div"
    count={count}
    page={page}
    onPageChange={onPageChange}
    rowsPerPage={rowsPerPage}
    onRowsPerPageChange={onRowsPerPageChange}
    rowsPerPageOptions={rowsPerPageOptions}
    sx={{
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        fontFamily: ff, fontSize: 12, color: '#718096',
      },
      '& .MuiTablePagination-select': { fontFamily: ff, fontSize: 12 },
    }}
  />
);

// ─── Empty State ────────────────────────────────────────────────
export const EmptyState = ({ message = 'No data found.', colSpan = 5 }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
      <Box sx={{ fontFamily: ff, fontSize: 13, color: '#A0AEC0' }}>{message}</Box>
    </TableCell>
  </TableRow>
);

// ─── Page Loader (modern shimmer bar) ───────────────────────────
export const PageLoader = () => (
  <Box sx={{ width: '100%', pt: 4 }}>
    {/* Shimmer bar at top */}
    <Box sx={{
      width: '100%', height: 3, borderRadius: 2, overflow: 'hidden', mb: 4,
      background: '#EDF2F7',
    }}>
      <Box sx={{
        width: '40%', height: '100%', borderRadius: 2,
        background: 'linear-gradient(90deg, #32A6D8, #005A64, #32A6D8)',
        backgroundSize: '200% 100%',
        animation: 'shimmerBar 1.5s ease-in-out infinite',
        '@keyframes shimmerBar': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(350%)' },
        },
      }} />
    </Box>
    {/* Skeleton cards */}
    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
      {[1, 2, 3, 4].map(i => (
        <Skeleton key={i} variant="rounded" width="25%" height={100} sx={{ borderRadius: '12px' }} />
      ))}
    </Box>
    {/* Skeleton content block */}
    <Skeleton variant="rounded" width="100%" height={300} sx={{ borderRadius: '12px', mb: 2 }} />
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Skeleton variant="rounded" width="50%" height={180} sx={{ borderRadius: '12px' }} />
      <Skeleton variant="rounded" width="50%" height={180} sx={{ borderRadius: '12px' }} />
    </Box>
  </Box>
);

// ─── Card Grid Skeleton ─────────────────────────────────────────
export const CardGridSkeleton = ({ cards = 4, height = 120 }) => (
  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
    {Array.from({ length: cards }).map((_, i) => (
      <Box key={i} sx={{ flex: '1 1 200px', minWidth: 180 }}>
        <Skeleton variant="rounded" height={height} sx={{ borderRadius: '14px' }} />
      </Box>
    ))}
  </Box>
);

// ─── Settings/Form Skeleton ─────────────────────────────────────
export const FormSkeleton = () => (
  <Box sx={{ pt: 2 }}>
    {[1, 2, 3].map(i => (
      <Box key={i} sx={{ mb: 3 }}>
        <Skeleton variant="rounded" width="100%" height={140} sx={{ borderRadius: '12px' }} />
      </Box>
    ))}
  </Box>
);

// ─── App Init Loader (for App.js) ───────────────────────────────
export const AppLoader = () => (
  <Box sx={{
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
    alignItems: 'center', height: '100vh', gap: 3,
  }}>
    <Box sx={{
      width: 48, height: 48, borderRadius: '12px',
      background: 'linear-gradient(135deg, #32A6D8 0%, #005A64 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'pulse 1.5s ease-in-out infinite',
      '@keyframes pulse': {
        '0%, 100%': { transform: 'scale(1)', opacity: 1 },
        '50%': { transform: 'scale(1.1)', opacity: 0.8 },
      },
    }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 5.172C10 3.782 8.423 2.679 6.5 3.019C4.577 3.359 3 5.3 3 7.121c0 2.39 1.942 4.409 4.275 6.083C8.598 14.13 10 15.573 10 15.573" />
        <path d="M14 5.172C14 3.782 15.577 2.679 17.5 3.019C19.423 3.359 21 5.3 21 7.121c0 2.39-1.942 4.409-4.275 6.083C15.402 14.13 14 15.573 14 15.573" />
        <path d="M8 14v.5M16 14v.5M11.25 16.25h1.5L12 17l-.75-.75Z" />
      </svg>
    </Box>
    <Box sx={{
      width: 120, height: 3, borderRadius: 2, overflow: 'hidden',
      background: '#EDF2F7',
    }}>
      <Box sx={{
        width: '40%', height: '100%', borderRadius: 2,
        background: 'linear-gradient(90deg, #32A6D8, #005A64)',
        animation: 'shimmerBar 1.2s ease-in-out infinite',
        '@keyframes shimmerBar': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(350%)' },
        },
      }} />
    </Box>
  </Box>
);

// ─── Page Title ─────────────────────────────────────────────────
export const PageTitle = ({ children }) => (
  <Box sx={{
    fontFamily: ff,
    fontSize: 13,
    fontWeight: 500,
    color: '#A0AEC0',
    mb: 2.5,
  }}>
    {children}
  </Box>
);

// ─── Status Chip ────────────────────────────────────────────────
export const StatusChip = ({ label, color, bgColor }) => (
  <Box sx={{
    display: 'inline-flex',
    px: 1.5,
    py: 0.25,
    borderRadius: '20px',
    background: bgColor || `${color}14`,
    fontFamily: ff,
    fontSize: 11,
    fontWeight: 600,
    color: color,
    lineHeight: '18px',
    whiteSpace: 'nowrap',
  }}>
    {label}
  </Box>
);
